import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Fuel, Infinity as InfinityIcon, ShieldCheck, Wrench, BadgeCheck } from "lucide-react";
import { getCars } from "../lib/api";
import { CarCard } from "../components/CarCard";

const HERO = "https://images.unsplash.com/photo-1600320254374-ce2d293c324e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    getCars().then((cars) => setFeatured(cars.filter((c) => c.featured).slice(0, 4)));
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative h-screen min-h-[640px] flex items-end">
        <div className="absolute inset-0">
          <img src={HERO} alt="Rideshare driver" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/50" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pb-24 w-full">
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="overline mb-6"
          >
            A+Auto Fleet — Rideshare Car Rentals
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter max-w-3xl"
          >
            Rent. Drive.<br />Start earning today.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.3 }}
            className="text-white/60 text-base max-w-xl mt-6 leading-relaxed"
          >
            Uber & Lyft-approved cars with unlimited mileage, insurance and maintenance included.
            No credit checks, no long-term contracts. Reserve online, sign your agreement, and pick
            up your keys — pay at pickup.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.45 }}
            className="flex flex-wrap gap-4 mt-10"
          >
            <button
              data-testid="hero-explore-btn"
              onClick={() => navigate("/fleet")}
              className="group bg-white text-black px-8 py-4 flex items-center gap-2 hover:bg-[#D4AF37] transition-colors"
            >
              Browse Available Cars
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Value props */}
      <section className="border-y border-white/10 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 lg:grid-cols-4 divide-y lg:divide-y-0 lg:divide-x divide-white/10">
          {[
            { icon: InfinityIcon, title: "Unlimited Miles", text: "Drive as much as you want. Zero per-mile fees, ever." },
            { icon: BadgeCheck, title: "Uber & Lyft Ready", text: "Every car is pre-approved and inspected for rideshare." },
            { icon: Wrench, title: "Maintenance Included", text: "Service, tires and 24/7 roadside — all on us." },
            { icon: Fuel, title: "Fuel-Smart Fleet", text: "High-MPG vehicles that keep more fuel money in your pocket." },
          ].map((v) => (
            <div key={v.title} className="p-10">
              <v.icon className="text-[#D4AF37]" size={26} strokeWidth={1.4} />
              <h3 className="font-serif text-2xl mt-5">{v.title}</h3>
              <p className="text-white/50 text-sm mt-3 leading-relaxed">{v.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured fleet */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="overline mb-4">Popular With Drivers</p>
            <h2 className="font-serif text-4xl lg:text-5xl tracking-tight">Cars built to earn</h2>
          </div>
          <Link
            to="/fleet"
            data-testid="view-all-fleet-link"
            className="hidden sm:flex items-center gap-2 text-white/60 hover:text-white text-sm"
          >
            View all <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.map((car, i) => (
            <CarCard key={car.slug} car={car} index={i} />
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="border-t border-white/10 bg-[#0A0A0A] py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="overline mb-4">How It Works</p>
          <h2 className="font-serif text-4xl lg:text-5xl tracking-tight mb-14">On the road in 3 steps</h2>
          <div className="grid md:grid-cols-3 gap-10">
            {[
              { n: "01", t: "Pick your car", d: "Browse Uber & Lyft-approved vehicles and choose one that fits your budget and shift." },
              { n: "02", t: "Sign online", d: "Complete a quick booking and accept your rental agreement — no paperwork, no branch visit." },
              { n: "03", t: "Pick up & drive", d: "Collect your keys at your chosen location, pay on the spot, and start accepting trips." },
            ].map((s) => (
              <div key={s.n}>
                <span className="font-serif text-5xl text-[#D4AF37]">{s.n}</span>
                <h3 className="font-serif text-2xl mt-4">{s.t}</h3>
                <p className="text-white/50 text-sm mt-3 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="relative py-28 border-t border-white/10 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1614091199036-e934784dbf0f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
          alt="" className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative max-w-3xl mx-auto text-center px-6">
          <h2 className="font-serif text-4xl lg:text-5xl tracking-tight">Your next shift starts here.</h2>
          <p className="text-white/60 mt-5">Reserve a rideshare-ready car in minutes. No deposit required.</p>
          <button
            data-testid="cta-reserve-btn"
            onClick={() => navigate("/fleet")}
            className="mt-8 bg-white text-black px-8 py-4 hover:bg-[#D4AF37] transition-colors"
          >
            Find Your Car
          </button>
        </div>
      </section>
    </div>
  );
}
